import React, { useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Typography,
  CircularProgress,
} from '@mui/material';
import { StackedDialog, useStackZIndex, useSelectMenuProps } from '../../utilities/ModalStackContext';
import { StateManager } from '../../utilities/stateManager';
import Store from '../../utilities/store';
import constants from '../../utilities/constants';

const EMPTY_FORM = {
  year: '',
  make: '',
  model: '',
  color: '',
  vin: '',
  location: 'FL',
  sellerName: '',
  phone_number: '',
  address1: '',
  city: '',
  state: '',
  zip: '',
  charge: '',
};

// Quick-add for a car that is on its way in. Creates the inventory record
// (status intake / sub_status inbound) and hands the caller a stop it can drop
// straight onto the load as a pickup.
const AddInboundCarForm = ({ open, onClose, onCreated }) => {
  // StackedDialog below picks this same z — pass it explicitly because this
  // component sits outside the provider it creates.
  const menuProps = useSelectMenuProps(useStackZIndex());
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.year.trim()) newErrors.year = 'Year is required';
    if (!formData.make.trim()) newErrors.make = 'Make is required';
    if (!formData.model.trim()) newErrors.model = 'Model is required';
    if (!formData.address1.trim()) newErrors.address1 = 'Pickup address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    if (saving) return;
    setFormData(EMPTY_FORM);
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const { stock } = await Store.createInboundCar({
        vin: formData.vin,
        year: formData.year,
        make: formData.make,
        model: formData.model,
        color: formData.color,
        location: formData.location,
      });

      // Same shape getInboundCars returns, so the selector list renders it
      // without a refetch.
      onCreated({
        id: stock,
        kind: 'inbound',
        car: {
          title: [formData.year, formData.make, formData.model].filter(Boolean).join(' '),
          thumbnail: null,
          vin: formData.vin.trim().toUpperCase(),
        },
        customer: {
          name: formData.sellerName.trim() || 'Consignor not set',
          address1: formData.address1.trim(),
          city: formData.city.trim(),
          state: formData.state.trim(),
          zip: formData.zip.trim(),
          phone_number: formData.phone_number.trim(),
          email: '',
        },
        consignorId: null,
        destination: formData.location,
        date: null,
        charge: formData.charge ? parseFloat(formData.charge) : 0,
      });

      StateManager.setAlertAndOpen(`${stock} created and added to the list`, 'success');
      setFormData(EMPTY_FORM);
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error creating inbound car:', error);
      StateManager.setAlertAndOpen('Could not create the inbound car. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <StackedDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Inbound Car</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              This creates a new stock number in intake with an inbound status, then adds it to the
              list as a pickup.
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <TextField
              name="year"
              label="Year"
              value={formData.year}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.year}
              helperText={errors.year}
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={4.5}>
            <TextField
              name="make"
              label="Make"
              value={formData.make}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.make}
              helperText={errors.make}
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={4.5}>
            <TextField
              name="model"
              label="Model"
              value={formData.model}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.model}
              helperText={errors.model}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              name="color"
              label="Color"
              value={formData.color}
              onChange={handleChange}
              fullWidth
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="vin"
              label="VIN"
              value={formData.vin}
              onChange={handleChange}
              fullWidth
              inputProps={{ style: { fontFamily: 'monospace', textTransform: 'uppercase' } }}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="inbound-destination-label">Destination Lot</InputLabel>
              <Select
                labelId="inbound-destination-label"
                name="location"
                label="Destination Lot"
                value={formData.location}
                onChange={handleChange}
                MenuProps={menuProps}
              >
                {constants.locations.map(loc => (
                  <MenuItem key={loc.value} value={loc.value}>{loc.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="charge"
              label="Shipping Charge"
              value={formData.charge}
              onChange={handleChange}
              fullWidth
              type="number"
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Pickup</Typography>
          </Grid>

          <Grid item xs={6}>
            <TextField
              name="sellerName"
              label="Seller / Consignor Name"
              value={formData.sellerName}
              onChange={handleChange}
              fullWidth
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="phone_number"
              label="Phone Number"
              value={formData.phone_number}
              onChange={handleChange}
              fullWidth
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="address1"
              label="Pickup Address"
              value={formData.address1}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.address1}
              helperText={errors.address1}
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              name="city"
              label="City"
              value={formData.city}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.city}
              helperText={errors.city}
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              name="state"
              label="State"
              value={formData.state}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.state}
              helperText={errors.state}
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="zip"
              label="Zip"
              value={formData.zip}
              onChange={handleChange}
              fullWidth
              autoComplete="off"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Create Car'}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
};

export default AddInboundCarForm;
